import { useState, useEffect } from 'react';
import { ChevronDown, Loader } from 'lucide-react';
import type { WarehouseTransfer, Warehouse } from '../../api/inventory';
import { fetchWarehouseTransfers, fetchWarehouses, updateTransferStatus } from '../../api/inventory';

interface WarehouseTransfersTableProps {
  refreshTrigger: number;
}

type StatusFilter = 'all' | 'pending' | 'shipped' | 'received';

export default function WarehouseTransfersTable({ refreshTrigger }: WarehouseTransfersTableProps) {
  const [transfers, setTransfers] = useState<WarehouseTransfer[]>([]);
  const [warehouses, setWarehouses] = useState<Map<string, Warehouse>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, [refreshTrigger]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [transfersData, warehousesData] = await Promise.all([
        fetchWarehouseTransfers(),
        fetchWarehouses(),
      ]);
      setTransfers(transfersData);
      const whMap = new Map(warehousesData.map(w => [w.id, w]));
      setWarehouses(whMap);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load transfers');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (transferId: string, newStatus: 'pending' | 'shipped' | 'received') => {
    setUpdatingId(transferId);
    try {
      await updateTransferStatus(transferId, newStatus);
      setTransfers(prev =>
        prev.map(t => t.id === transferId ? { ...t, status: newStatus } : t)
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update transfer status');
    } finally {
      setUpdatingId(null);
    }
  };

  const filteredTransfers = transfers.filter(t =>
    statusFilter === 'all' ? true : t.status === statusFilter
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400 border-yellow-200 dark:border-yellow-800';
      case 'shipped':
        return 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800';
      case 'received':
        return 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800';
      default:
        return 'bg-stone-50 dark:bg-stone-900/20 text-stone-700 dark:text-stone-400 border-stone-200 dark:border-stone-800';
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString([], {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-14 rounded-lg bg-stone-100 dark:bg-stone-800 animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-4 py-3 text-sm text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-2">
        {(['all', 'pending', 'shipped', 'received'] as StatusFilter[]).map(status => (
          <button
            key={status}
            onClick={() => setStatusFilter(status)}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition ${
              statusFilter === status
                ? 'border-moss-500 bg-moss-50 dark:bg-moss-900/20 text-moss-700 dark:text-moss-400'
                : 'border-stone-300 dark:border-stone-600 bg-white dark:bg-stone-800 text-stone-600 dark:text-stone-400 hover:border-stone-400 dark:hover:border-stone-500'
            }`}
          >
            {status === 'all' ? 'All' : status.charAt(0).toUpperCase() + status.slice(1)}
          </button>
        ))}
      </div>

      {/* Empty state */}
      {filteredTransfers.length === 0 ? (
        <div className="text-center py-8 rounded-lg bg-stone-50 dark:bg-stone-800/50 border border-stone-200 dark:border-stone-700">
          <p className="text-sm text-stone-600 dark:text-stone-400">No transfers found</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredTransfers.map(transfer => {
            const sourceWh = warehouses.get(transfer.sourceWarehouseId);
            const destWh = warehouses.get(transfer.destWarehouseId);
            const isExpanded = expandedId === transfer.id;

            return (
              <div
                key={transfer.id}
                className="rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 overflow-hidden"
              >
                {/* Summary row */}
                <button
                  onClick={() => setExpandedId(isExpanded ? null : transfer.id)}
                  className="w-full px-4 py-3 flex items-center gap-3 hover:bg-stone-50 dark:hover:bg-stone-700/50 transition"
                >
                  <ChevronDown
                    size={16}
                    className={`text-stone-400 transition ${isExpanded ? 'rotate-180' : ''}`}
                  />
                  <div className="flex-1 text-left min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium text-stone-900 dark:text-stone-100">
                        {sourceWh?.name || 'Unknown'} → {destWh?.name || 'Unknown'}
                      </span>
                      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium border ${getStatusColor(transfer.status)}`}>
                        {transfer.status}
                      </span>
                    </div>
                    <p className="text-xs text-stone-600 dark:text-stone-400">
                      {transfer.quantity} units • {new Date(transfer.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                </button>

                {/* Details */}
                {isExpanded && (
                  <div className="border-t border-stone-200 dark:border-stone-700 px-4 py-3 space-y-3">
                    {/* Transfer info */}
                    <div className="grid grid-cols-2 gap-4 text-xs">
                      <div>
                        <p className="text-stone-500 dark:text-stone-400 font-medium mb-0.5">From</p>
                        <p className="text-stone-900 dark:text-stone-100">
                          {sourceWh?.name} {sourceWh?.code && `(${sourceWh.code})`}
                        </p>
                      </div>
                      <div>
                        <p className="text-stone-500 dark:text-stone-400 font-medium mb-0.5">To</p>
                        <p className="text-stone-900 dark:text-stone-100">
                          {destWh?.name} {destWh?.code && `(${destWh.code})`}
                        </p>
                      </div>
                      <div>
                        <p className="text-stone-500 dark:text-stone-400 font-medium mb-0.5">Quantity</p>
                        <p className="text-stone-900 dark:text-stone-100">{transfer.quantity} units</p>
                      </div>
                      <div>
                        <p className="text-stone-500 dark:text-stone-400 font-medium mb-0.5">Created</p>
                        <p className="text-stone-900 dark:text-stone-100">{formatDate(transfer.createdAt)}</p>
                      </div>
                      {transfer.shippedAt && (
                        <div>
                          <p className="text-stone-500 dark:text-stone-400 font-medium mb-0.5">Shipped</p>
                          <p className="text-stone-900 dark:text-stone-100">{formatDate(transfer.shippedAt)}</p>
                        </div>
                      )}
                      {transfer.receivedAt && (
                        <div>
                          <p className="text-stone-500 dark:text-stone-400 font-medium mb-0.5">Received</p>
                          <p className="text-stone-900 dark:text-stone-100">{formatDate(transfer.receivedAt)}</p>
                        </div>
                      )}
                    </div>

                    {/* Notes */}
                    {transfer.notes && (
                      <div>
                        <p className="text-xs text-stone-500 dark:text-stone-400 font-medium mb-1">Notes</p>
                        <p className="text-xs text-stone-900 dark:text-stone-100 bg-stone-50 dark:bg-stone-700 rounded px-2.5 py-1.5">
                          {transfer.notes}
                        </p>
                      </div>
                    )}

                    {/* Status progression */}
                    <div className="space-y-2">
                      <p className="text-xs text-stone-500 dark:text-stone-400 font-medium">Update Status</p>
                      <div className="flex gap-2">
                        {(['pending', 'shipped', 'received'] as const).map(status => (
                          <button
                            key={status}
                            onClick={() => handleStatusChange(transfer.id, status)}
                            disabled={updatingId === transfer.id}
                            className={`px-3 py-1.5 text-xs rounded-lg border font-medium transition ${
                              transfer.status === status
                                ? 'border-moss-500 bg-moss-50 dark:bg-moss-900/20 text-moss-700 dark:text-moss-400 cursor-default'
                                : 'border-stone-300 dark:border-stone-600 text-stone-700 dark:text-stone-300 hover:border-stone-400 dark:hover:border-stone-500 disabled:opacity-50'
                            }`}
                          >
                            {updatingId === transfer.id ? (
                              <Loader size={12} className="inline animate-spin mr-1" />
                            ) : null}
                            {status.charAt(0).toUpperCase() + status.slice(1)}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
